'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  getCurrentUser,
  getAuthTokens,
  clearAuthTokens,
  type AuthUser,
} from '../lib/auth';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  refreshUser: async () => {},
  setUser: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const tokens = getAuthTokens();
      if (tokens) {
        const currentUser = await getCurrentUser();
        // Only update user if we got a valid user object
        // If currentUser is null, it means token is invalid, but don't clear immediately
        if (currentUser) {
          setUser(currentUser);
        } else {
          // Token might be expired, try to refresh first before clearing
          // Don't clear tokens on null response - might be temporary network issue
          console.warn('getCurrentUser returned null, but keeping tokens (might be temporary)');
        }
      } else {
        setUser(null);
      }
    } catch (error: any) {
      // Only clear tokens if it's an actual authentication error
      // getCurrentUser() now throws errors with 'AUTH_ERROR:' prefix for auth issues
      const isAuthError = error?.message?.includes('AUTH_ERROR') ||
                         error?.message?.includes('401') || 
                         error?.message?.includes('403') ||
                         error?.message?.includes('Unauthorized') ||
                         error?.message?.includes('Forbidden') ||
                         error?.message?.includes('Not authenticated');
      
      if (isAuthError) {
        console.log('Authentication error detected, clearing tokens');
        clearAuthTokens();
        setUser(null);
      } else {
        // Network error or other temporary issue - keep user logged in
        // Don't log as error, just warn - this is expected for network issues
        if (error?.message) {
          console.warn('Non-auth error during auth check, keeping user logged in:', error.message);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      await checkAuth();
      if (!mounted) {
        return;
      }
    };

    // Check auth on mount only
    // Token refresh is handled automatically in getIdToken() when needed
    // API calls will validate tokens and return 401/403 if invalid
    initAuth();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}; 